import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { MagnifyingGlass, Moon, Star, Sun, WarningCircle } from '@phosphor-icons/react';
import { DetailDrawer } from './components/DetailDrawer.js';
import { CardThumbnailPreview } from './components/CardThumbnailPreview.js';
import {
  filterCards,
  formatProviderTimestamp,
  formatReturn,
  formatUsd,
  loadTheme,
  loadWatchlist,
  mapWithConcurrency,
  reconcileFilters,
  saveTheme,
  saveWatchlist,
  sourceFreshness,
  returnTone,
} from './lib/screener.js';
import type { Card, Filters, ReturnValue, Status, Theme } from './types.js';

const games = [
  ['', 'All games'],
  ['magic', 'Magic'],
  ['pokemon', 'Pokémon'],
  ['onepiece', 'One Piece'],
  ['riftbound', 'Riftbound'],
] as const;
const baseFilters: Filters = {
  game: '',
  language: '',
  query: '',
  set: '',
  finish: '',
  minPrice: '',
  watchedOnly: false,
};
const column = createColumnHelper<Card>();
const PAGE_SIZE = 30;
const API_PAGE_SIZE = 200;
const LOAD_TIMEOUT_MS = 20_000;
const gameLabels: Record<string, string> = {
  magic: 'Magic',
  pokemon: 'Pokémon',
  onepiece: 'One Piece',
  riftbound: 'Riftbound',
};

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function responseJson<T>(response: Response, message: string): Promise<T> {
  if (!response.ok) throw new Error(message);
  return response.json() as Promise<T>;
}

function ReturnCell({ result, label }: { result: ReturnValue; label: string }) {
  const explanation = result.reason ?? `Baseline ${result.baselineDate?.slice(0, 10)}`;
  return (
    <span
      className={returnTone(result.value)}
      title={explanation}
      tabIndex={0}
      aria-label={`${label}: ${result.value === null ? 'unavailable' : formatReturn(result.value)}. ${explanation}`}
    >
      {formatReturn(result.value)}
    </span>
  );
}

export default function App() {
  const [cards, setCards] = useState<Card[]>([]);
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [filters, setFilters] = useState(baseFilters);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'price', desc: true }]);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Card | null>(null);
  const [theme, setTheme] = useState<Theme>(() => {
    const local = storage();
    return local ? loadTheme(local) : 'dark';
  });
  const [watch, setWatch] = useState(() => {
    const local = storage();
    return new Set(local ? loadWatchlist(local) : []);
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const local = storage();
    if (local) saveTheme(local, theme);
  }, [theme]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
    let active = true;
    setLoading(true);
    setError('');
    void (async () => {
      const [first, nextStatus] = await Promise.all([
        fetch(`/api/cards?limit=${API_PAGE_SIZE}`, { signal: controller.signal }).then(response => responseJson<{ items: Card[]; total: number }>(response, 'The tracked universe could not be loaded.')),
        fetch('/api/status', { signal: controller.signal }).then(response => responseJson<Status>(response, 'Provider status could not be loaded.')),
      ]);
      const offsets = Array.from(
        { length: Math.max(0, Math.ceil(first.total / API_PAGE_SIZE) - 1) },
        (_unused, index) => (index + 1) * API_PAGE_SIZE,
      );
      const remaining = await mapWithConcurrency(offsets, 4, async offset => {
        const response = await fetch(`/api/cards?limit=${API_PAGE_SIZE}&offset=${offset}`, { signal: controller.signal });
        return responseJson<{ items: Card[] }>(response, 'The tracked universe could not be loaded completely.');
      });
      if (!active) return;
      const allCards = [...first.items, ...remaining.flatMap(result => result.items)];
      if (allCards.length !== first.total) throw new Error('The tracked universe response was incomplete.');
      setCards(allCards);
      setStatus(nextStatus);
      setFilters(current => reconcileFilters(current, allCards));
    })().catch((caught: unknown) => {
      if (!active) return;
      const failure = caught as Error;
      setError(failure.name === 'AbortError'
        ? 'The data request timed out. Check the local service and retry.'
        : failure.message || 'The data request failed.');
    }).finally(() => {
      window.clearTimeout(timer);
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [retryKey]);

  const toggleWatch = useCallback((id: string) => {
    setWatch(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      const local = storage();
      if (local) saveWatchlist(local, next);
      return next;
    });
  }, []);

  const result = useMemo(() => filterCards(cards, filters, watch), [cards, filters, watch]);
  const scopedCards = useMemo(() => cards.filter(card => (
    (!filters.game || card.game === filters.game)
    && (!filters.language || card.language === filters.language)
  )), [cards, filters.game, filters.language]);
  const sets = useMemo(() => [...new Set(scopedCards.map(card => card.setName))].sort(), [scopedCards]);
  const finishes = useMemo(() => [...new Set(scopedCards.map(card => card.finish))].sort(), [scopedCards]);

  const columns = useMemo(() => [
    column.accessor('name', {
      header: 'Card / printing',
      cell: info => (
        <div className="card-cell">
          {info.row.original.imageUrl
            ? <CardThumbnailPreview
                key={info.row.original.id}
                imageUrl={info.row.original.imageUrl}
                caption={`${info.row.original.name} · ${info.row.original.setName} · ${info.row.original.language === 'ja' ? 'Japanese (JA)' : info.row.original.language.toUpperCase()} · ${info.row.original.finish}`}
                onOpen={() => setSelected(info.row.original)}
              />
            : <span className="thumb-empty" aria-hidden="true" />}
          <div className="card-identity">
            <button
              type="button"
              className="card-name-button"
              title={info.getValue()}
              onClick={() => setSelected(info.row.original)}
            >
              {info.getValue()}
            </button>
            <small>
              {gameLabels[info.row.original.game] ?? info.row.original.game} · {info.row.original.setCode} · #{info.row.original.collectorNumber} ·{' '}
              {info.row.original.language === 'ja' ? 'Japanese (JA)' : info.row.original.language.toUpperCase()}
            </small>
            <div className="mobile-row-summary">
              <span><small>Market</small><strong className="money">{info.row.original.price === null ? '—' : formatUsd(info.row.original.price)}</strong></span>
              <span><small>24h</small><ReturnCell result={info.row.original.returns.day} label="24 hour daily return" /></span>
            </div>
          </div>
        </div>
      ),
    }),
    column.accessor('finish', { header: 'Finish' }),
    column.accessor(row => row.price ?? undefined, {
      id: 'price',
      header: 'Market',
      sortUndefined: 'last',
      cell: info => (
        <strong className="money">
          {info.row.original.price === null ? '—' : formatUsd(info.row.original.price)}
        </strong>
      ),
    }),
    ...([
      ['day', '24h daily', '24 hour daily return'],
      ['week', '7d', '7 day return'],
      ['month', '30d', '30 day return'],
      ['year', '1y', '1 year return'],
      ['fiveYear', '5y', '5 year return'],
    ] as const).map(([key, header, label]) => column.accessor(row => row.returns[key].value ?? undefined, {
      id: `returns.${key}`,
      header,
      sortUndefined: 'last',
      cell: info => <ReturnCell result={info.row.original.returns[key]} label={label} />,
    })),
    column.display({
      id: 'watch',
      header: '',
      cell: info => (
        <button
          type="button"
          className="star"
          aria-label={watch.has(info.row.original.id) ? 'Remove from watchlist' : 'Add to watchlist'}
          onClick={() => toggleWatch(info.row.original.id)}
        >
          <Star weight={watch.has(info.row.original.id) ? 'fill' : 'regular'} />
        </button>
      ),
    }),
  ], [toggleWatch, watch]);

  const table = useReactTable({
    data: result.items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const sortedRows = table.getRowModel().rows;
  const pages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const visible = sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    if (page >= pages) setPage(Math.max(0, pages - 1));
  }, [page, pages]);

  function update<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters(previous => {
      const next = { ...previous, [key]: value };
      return key === 'game' || key === 'language' ? reconcileFilters(next, cards) : next;
    });
    setPage(0);
  }

  const freshness = sourceFreshness(status?.providerUpdatedAt ?? null);
  const freshnessLabel = freshness === 'stale'
    ? 'Stale snapshot (48h+)'
    : freshness === 'live' ? 'Snapshot age under 48h' : 'Snapshot age unavailable';
  const providerLabel = status?.providerUpdatedAt
    ? `Provider snapshot time ${formatProviderTimestamp(status.providerUpdatedAt)}`
    : 'Provider snapshot time unavailable';

  return (
    <>
      <div className="app-shell">
        <header className="topbar">
          <div>
            <h1>TCG Screener</h1>
            <p>Exact printings · US-market USD</p>
          </div>
          <div className="source-status">
            <span className={freshness} aria-hidden="true" />
            <div>
              <b>{freshnessLabel} · {providerLabel}</b>
              <small>
                {status
                  ? `${status.pricedVariantCount.toLocaleString()} priced · ${status.unpricedVariantCount.toLocaleString()} unpriced active variants`
                  : 'Awaiting local data'}
              </small>
            </div>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={() => setTheme(current => current === 'dark' ? 'light' : 'dark')}
            aria-label={`Use ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </button>
        </header>

        <section className="scope" aria-label="Catalog scope">
          <div>
            <b>{status ? `${status.trackedSetCount} imported sets` : 'Imported set count unavailable'}</b>
            <span>Active bounded coverage, not the full catalog</span>
          </div>
          <div>
            <b>TCGplayer Market</b>
            <span>Condition aggregate · manual capture, not scheduled</span>
          </div>
          <div className="gap-note">
            <WarningCircle />
            <span>
              <b>Japanese One Piece unavailable</b>
              Current free feed has no dedicated catalog; English is never substituted.
            </span>
          </div>
        </section>

        <nav className="game-tabs" aria-label="Game">
          {games.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={filters.game === value ? 'active' : ''}
              aria-current={filters.game === value ? 'page' : undefined}
              onClick={() => update('game', value)}
            >
              {label}
            </button>
          ))}
        </nav>

        <main>
          <div className="controls">
            <label className="search">
              <MagnifyingGlass />
              <span className="sr-only">Search cards</span>
              <input
                value={filters.query}
                onChange={event => update('query', event.target.value)}
                placeholder="Search card, set, or collector number"
              />
            </label>
            <select aria-label="Language" value={filters.language} onChange={event => update('language', event.target.value)}>
              <option value="">All languages</option>
              <option value="en">English</option>
              <option value="ja">Japanese</option>
            </select>
            <select aria-label="Set" value={filters.set} onChange={event => update('set', event.target.value)}>
              <option value="">All imported sets</option>
              {sets.map(setName => <option key={setName}>{setName}</option>)}
            </select>
            <select aria-label="Finish" value={filters.finish} onChange={event => update('finish', event.target.value)}>
              <option value="">All finishes</option>
              {finishes.map(finish => <option key={finish}>{finish}</option>)}
            </select>
            <label className="min-price">
              <span>$</span>
              <input
                aria-label="Minimum price"
                type="number"
                min="0"
                step="0.01"
                placeholder="Min price"
                value={filters.minPrice}
                onChange={event => update('minPrice', event.target.value)}
              />
            </label>
            <button
              type="button"
              className={filters.watchedOnly ? 'watch-filter active' : 'watch-filter'}
              onClick={() => update('watchedOnly', !filters.watchedOnly)}
            >
              <Star weight={filters.watchedOnly ? 'fill' : 'regular'} />
              Watchlist <span>{watch.size}</span>
            </button>
          </div>

          {loading ? (
            <div className="state" role="status">Loading active provider observations…</div>
          ) : error ? (
            <div className="state error" role="alert">
              <WarningCircle />
              <h2>Tracked data could not be loaded</h2>
              <p>{error}</p>
              <button type="button" onClick={() => setRetryKey(key => key + 1)}>Retry</button>
            </div>
          ) : result.gap ? (
            <div className="state gap">
              <h2>Japanese One Piece is not feed-covered</h2>
              <p>{result.gap} A provider with verified authenticated US-market USD coverage is still required.</p>
            </div>
          ) : result.items.length === 0 ? (
            <div className="state">
              <h2>No active variants match</h2>
              <p>Adjust the filters. Missing data is not treated as a zero price.</p>
            </div>
          ) : (
            <>
              <div className="table-meta">
                <span>{result.items.length.toLocaleString()} matching active variants</span>
                <span>Returns use exact daily observations; hover or focus values for baseline details</span>
                <span className="mobile-table-hint">Swipe tabs or table horizontally · Tap a card name for details</span>
              </div>
              <div className="table-scroll" tabIndex={0} aria-label="Card screener table">
                <table>
                  <thead>
                    {table.getHeaderGroups().map(group => (
                      <tr key={group.id}>
                        {group.headers.map(header => (
                          <th
                            key={header.id}
                            aria-sort={header.column.getIsSorted() === 'asc'
                              ? 'ascending'
                              : header.column.getIsSorted() === 'desc' ? 'descending' : 'none'}
                          >
                            {header.column.getCanSort() ? (
                              <button type="button" onClick={header.column.getToggleSortingHandler()}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getIsSorted() === 'asc' ? ' ↑' : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                              </button>
                            ) : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {visible.map(row => (
                      <tr key={row.id} onDoubleClick={() => setSelected(row.original)}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                <button type="button" disabled={page === 0} onClick={() => setPage(current => current - 1)}>Previous</button>
                <span>Page {page + 1} of {pages}</span>
                <button type="button" disabled={page >= pages - 1} onClick={() => setPage(current => current + 1)}>Next</button>
              </div>
            </>
          )}
        </main>
        <footer>
          Read-only research view · Provider snapshots are not live ticks or last-sale times · Watchlist stays in this browser
        </footer>
      </div>
      {selected && (
        <DetailDrawer
          card={selected}
          onClose={() => setSelected(null)}
          watched={watch.has(selected.id)}
          onToggle={() => toggleWatch(selected.id)}
        />
      )}
    </>
  );
}
